import { v4 as uuid } from 'uuid'
import parseUserAgent from 'ua-parser-js'
import { sentryService } from '../SentryService'

import { getNowUnixTimestamp } from '../../utils/dateUtils'
import { _analytics } from './AnalyticsImpl'
import { brazeWhitelistAttributes } from './platforms/brazeWhitelistAttributes'
import { filterObjectBlankAttributes } from '../../utils/utils'
import StorageManager from '../StorageManager'
import { _EVENT_NAMES, _SCREENS } from './AnalyticsEvents'
import { logger } from '../logger'
import { ATTRIBUTION_SURVEY_KEY } from '../../pages/EmailCapturePage/EmailCapturePage.consts'
import { intercomService } from './platforms/intercom'
import { initFacebook } from './platforms/facebook'
import { capturedUserData } from '../../utils/ApiUtils'
import { updateAmplitudeUserProperties } from './platforms/amplitude'
import { getBrazeInstance } from './platforms/braze.client'

export { _EVENT_NAMES as EVENT_NAMES, _SCREENS as SCREENS }

const LOG_ANALYTICS = false

export const FACEBOOK_INITIATE_CHECKOUT_EVENT_NAME = 'InitiateCheckout'
export const FACEBOOK_PURCHASE_EVENT_NAME = 'Purchase'

class Analytics {
  async init(serverDeviceId, username, email) {
    this.braze = getBrazeInstance()
    await _analytics.init(serverDeviceId)
    initFacebook(username, email)
  }

  /**
   * @param {string} userId
   * @param {string | null | undefined} [email]
   * @param {boolean | null | undefined} [emailConsent]
   */
  async identifyUser(userId, email = null, emailConsent = null) {
    await _analytics.identifyUser(userId, email, emailConsent)
    sentryService.identifyUser(userId)
  }

  setUserEmail({ email, isNewEmail, trackEventName, trackProperties = {} }) {
    _analytics.setUserEmail({
      email,
      isNewEmail,
      trackEventName,
      trackProperties,
    })
  }

  setCountryCode(countryCode) {
    _analytics.setUserCountryCode(countryCode)
  }

  setUserFullName(name) {
    const [firstName, ...lastName] = name.split(' ')
    const user = this.braze.getUser()
    if (!user) {
      logger.warning('Braze user not available for setFirstName/setLastName')
    }
    user.setFirstName(firstName)
    user.setLastName(lastName.join(' '))
    intercomService.setName(name)
  }

  setLanguage(language) {
    const user = this.braze.getUser()
    if (!user) {
      logger.warning('Braze user not available for setLanguage')
    }
    user.setLanguage(language)
  }

  setPhoneNumber(number) {
    _analytics.setPhoneNumber(number)
  }

  syncPhoneToAdNetworks(number) {
    _analytics.syncPhoneToAdNetworks(number)
  }

  async setUserProperties(userProperties) {
    await _analytics.setUserProperties(userProperties)
  }

  setActiveExperiments(userProperties) {
    intercomService.setActiveExperiments(userProperties)
  }

  setUserAgentInformation() {
    try {
      const uaInfo = parseUserAgent()
      logger.info('User agent', { uaInfo })
      updateAmplitudeUserProperties({
        set: {
          'UserAgent OS': `${uaInfo.os.name} ${uaInfo.os.version}`,
          'UserAgent Engine': `${uaInfo.engine.name} ${uaInfo.engine.version}`,
          'UserAgent Browser': `${uaInfo.browser.name} ${uaInfo.browser.version}`,
        },
      })
    } catch (e) {
      logger.error('Failed to save user agent information to amplitude', e)
    }
  }

  /**
   * @param {string} eventName
   * @param {object} [eventProperties]
   * @param {{ serverSideAnalyticsPlatforms?: string[], eventID?: string }} [options]
   */
  track(
    eventName,
    eventProperties = {},
    { serverSideAnalyticsPlatforms = [], eventID = uuid() } = {}
  ) {
    // Prevents amplitude warnings for empty attributes
    const cleanEventProperties = filterObjectBlankAttributes(eventProperties)
    _analytics.track(eventName, cleanEventProperties, {
      serverSideAnalyticsPlatforms,
      eventID,
    })

    if (LOG_ANALYTICS && window.KEYS.runtimeEnv !== 'production') {
      logger.info(`[Event] ${eventName}`, { eventProperties })
    }
  }

  trackOnce(eventName, eventProperties) {
    const cleanEventProperties = filterObjectBlankAttributes(eventProperties)
    _analytics.trackOnce(eventName, cleanEventProperties)
  }

  trackOncePerId(eventName, id, eventProperties) {
    const cleanEventProperties = filterObjectBlankAttributes(eventProperties)
    _analytics.trackOncePerId(eventName, id, cleanEventProperties)
  }

  trackScreenView(screenName, eventProperties = {}) {
    _analytics.trackScreenView(screenName, eventProperties)
  }

  facebookPixelTrackOnceInSession(eventName, data = {}) {
    _analytics.facebookPixelTrackOnceInSession(eventName, data)
  }

  facebookTrack(eventName, data = {}, eventID = uuid()) {
    _analytics.facebookTrack(
      eventName,
      {
        ...data,
        attribution_survey_answer:
          StorageManager.get(ATTRIBUTION_SURVEY_KEY) || null,
      },
      eventID
    )
  }

  // This method tracks regular client side analytics but only tracks once per session to the server.
  // This is useful for high-load, frequent actions, like "Photo Picked"
  trackWithOneTimeServerSide(
    eventName,
    eventProperties,
    serverSideAnalyticsPlatforms = []
  ) {
    _analytics.trackWithOneTimeServerSide(
      eventName,
      eventProperties,
      serverSideAnalyticsPlatforms
    )
  }

  trackSmsScreenLastSeen() {
    // Only send whitelisted attributes to Braze
    if (!brazeWhitelistAttributes.includes('sms_screen_last_seen')) return
    const user = this.braze?.getUser()
    if (!user) {
      logger.warning('Braze user not available for trackSmsScreenLastSeen')
      return
    }
    user.setCustomUserAttribute('sms_screen_last_seen', getNowUnixTimestamp())
  }

  trackEmailCaptureTermsOfUseTapped() {
    _analytics.trackEmailCaptureTermsOfUseTapped()
  }

  trackEmailCapturePrivacyPolicyTapped() {
    _analytics.trackEmailCapturePrivacyPolicyTapped()
  }

  trackFirstInteraction() {
    _analytics.trackFirstInteraction()
  }

  trackFirstOpen() {
    _analytics.trackFirstOpen()
  }

  trackOncePerSession(event, eventProperties = {}, options = {}) {
    _analytics.trackOncePerSession(event, eventProperties, options)
  }

  capturePhoneNumber(
    phoneNumber,
    didConsentToMarketingTexts,
    source,
    onboardingFlow
  ) {
    if (phoneNumber) {
      this.setPhoneNumber(phoneNumber)
      const eventProperties = {
        Source: source,
        Phone: phoneNumber,
        'Is Explicit Consent Given': didConsentToMarketingTexts,
        'Onboarding Flow': onboardingFlow,
      }
      this.track(_EVENT_NAMES.PHONE_NUMBER_CAPTURED, eventProperties)

      if (didConsentToMarketingTexts) {
        this.track('Phone Number Consent Given', eventProperties)
      }
    }

    capturedUserData({ phoneNumber })
  }
}

export const analytics = new Analytics()
